// In-band opportunities hooks — the WRAPPER layer (lint-exempt for TanStack Query).
// Opportunities are band-scoped (RESTRICTIVE RLS: creator + invitees), so the list key is the tenant-keyed
// ['band', bandId, 'opportunities']; the frozen `opportunity-changed` signal (ac.ui.band.{id} + ac.ui.user.{id},
// ) invalidates it — see the invalidationRegistry `invalidateOpportunities` handler. MONEY-FREE.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type OpportunityDto = components["schemas"]["OpportunityDto"];
type OpportunityDtoPaged = components["schemas"]["OpportunityDtoPaged"];
type OpportunityInviteDto = components["schemas"]["OpportunityInviteDto"];
export type CreateOpportunityRequest = components["schemas"]["CreateOpportunityRequest"];
export type UpdateOpportunityRequest = components["schemas"]["UpdateOpportunityRequest"];

/** GET the band's in-band opportunities + their invites. `enabled` lets a money-gated consumer defer
 *  the fetch until its own gate opens (the Booking dashboard self-gates on money:lifetime before it ever reads). */
export function useOpportunities(opts: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<OpportunityDtoPaged>(
    ["opportunities"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/opportunities", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled: opts.enabled ?? true },
  );
}

/**
 * `POST /bands/{bandId}/opportunities` (201 → OpportunityDto) — create an in-band opportunity (the Booking
 * deals board's "+ New offer"). Server always seeds `status='open'` (IOpportunitiesService).
 * MONEY-FREE (the DTO carries no money field). Server-confirm then repaint (NOT optimistic — a create mints a
 * server id) — invalidate the band's opportunities list. Error surfaces verbatim as a toast.
 */
export function useCreateOpportunity() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<OpportunityDto, CreateOpportunityRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/opportunities", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
        // The roster artist page lists opportunities from its composed dashboard read, not this list (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
        void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
        // A dated opportunity is a row on the ops calendar (the mobile Schedule "+" creates one), so the
        // calendar has to repaint too — its key is separate from the opportunities list.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't create the deal")),
    },
  );
}

/**
 * `PUT /bands/{bandId}/opportunities/{oppId}` (→ OpportunityDto) — move a deal to a new pipeline `status`
 * (open → confirmed → completed, or cancelled; the "promote" gate). The backend PUT is a FULL replace
 * (`UpdateOpportunityAsync` overwrites every field), so the caller MUST send the opportunity's complete current
 * field set + the new status — a partial `{status}` would clobber the date/type. CREATOR-ONLY server-side (a
 * non-creator gets 403); callers gate the control on `createdByPersonId`. MONEY-FREE. Server-confirm then
 * repaint — invalidate the band's opportunities list. Error (403/404/422) surfaces verbatim as a toast.
 */
export function useUpdateOpportunityStatus() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<OpportunityDto, { oppId: string; body: UpdateOpportunityRequest }>(
    async (band, { oppId, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/opportunities/{oppId}", {
        params: { path: { bandId: band, oppId } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
        // The roster artist page lists opportunities from its composed dashboard read, not this list (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
        void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
        // Date/type/title all change the calendar row.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't move the deal")),
    },
  );
}

/**
 * Rebuild the FULL {@link UpdateOpportunityRequest} from an existing opportunity + a new status — the safe
 * payload for a status-move against the full-replace PUT (see {@link useUpdateOpportunityStatus}).
 */
export function opportunityStatusUpdate(o: OpportunityDto, status: string): UpdateOpportunityRequest {
  return {
    type: o.type ?? "other",
    prSubtype: o.prSubtype,
    prFormat: o.prFormat,
    date: o.date,
    title: o.title,
    startTime: o.startTime,
    endTime: o.endTime,
    locationName: o.locationName,
    address: o.address,
    lat: o.lat,
    lng: o.lng,
    meetingLink: o.meetingLink,
    notes: o.notes,
    status,
  };
}

/**
 * `POST /opportunities/{oppId}/invite` — the invitee SET the opportunity should end with (not an append).
 * Dropped invitees are uninvited: their invite row goes, and an accepted invitee's personal calendar row is
 * removed through the `artist.uninvite_opportunity` gate (person-scoped RLS — the creator's own context
 * cannot touch it). Creator-only server-side. At least one invitee is required — "remove everyone" is what
 * DELETE is for, not an empty set.
 */
export function useSetOpportunityInvitees() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<OpportunityDto, { oppId: string; personIds: string[] }>(
    async (band, { oppId, personIds }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/opportunities/{oppId}/invite", {
        params: { path: { bandId: band, oppId } },
        body: { invitees: personIds.map((personId) => ({ personId })) },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
        // The roster artist page lists opportunities from its composed dashboard read, not this list (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
        void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't update the invitees")),
    },
  );
}

/**
 * `DELETE /opportunities/{oppId}` (204) — cancel the opportunity outright. Creator-only server-side; the
 * caller confirms first (ConfirmDialog) because this is the destructive verb — invitees lose it from their
 * dashboards and calendars, and there is no undo.
 */
export function useDeleteOpportunity() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<void, string>(
    async (band, oppId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/opportunities/{oppId}", {
        params: { path: { bandId: band, oppId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
        // The roster artist page lists opportunities from its composed dashboard read, not this list (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
        void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
        // Cancelling takes the row off the calendar too.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't cancel the opportunity")),
    },
  );
}

/**
 * `POST /opportunities/{oppId}/reinvite` — ask people who DECLINED to look again. Creator-only server-side.
 *
 * Deliberately NOT the invitee SET above: that body is the list the opportunity should end with, and the
 * editor's picker opens prefilled with everyone on it — so re-opening a pass there would re-ask every
 * decliner on a save that merely moved the date. Asking again is its own act, exactly as it is on the show
 * side (`PUT members` replaces a roster and resets nobody; `POST members/invite` is the ask).
 *
 * The server replaces the spent invite with a fresh one, so the person is notified rather than silently
 * re-listed. Server-confirm then repaint — the same two keys the RSVP invalidates, because the row returns
 * to that person's own dashboard. A stranger (never invited) is a 422 and surfaces verbatim.
 */
export function useReinviteOpportunity() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<OpportunityDto, { oppId: string; personIds: string[] }>(
    async (band, { oppId, personIds }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/opportunities/{oppId}/reinvite", {
        params: { path: { bandId: band, oppId } },
        body: { invitees: personIds.map((personId) => ({ personId })) },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
        // The roster artist page lists opportunities from its composed dashboard read, not this list (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
        void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't re-invite")),
    },
  );
}

/**
 * POST the caller's RSVP to an opportunity (`{ response: 'in' | 'out' }` — the SAME I'M IN / Pass
 * vocabulary the backend validates and the members RSVP uses). Server-confirm then repaint — invalidate the
 * band list AND the person-scoped ['me','opportunities'] key (an invite is the person's, cross-band, so it
 * lives outside ['band', …] to survive a band switch, like notifications).
 */
export function useRespondOpportunity() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<OpportunityInviteDto, { oppId: string; response: "in" | "out" }>(
    async (band, { oppId, response: reply }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/opportunities/{oppId}/respond",
        { params: { path: { bandId: band, oppId } }, body: { response: reply } },
      );
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "opportunities"] });
        // The roster artist page lists opportunities from its composed dashboard read, not this list (ART-338).
        void qc.invalidateQueries({ queryKey: ["band", bandId, "roster", "dashboard"] });
        void qc.invalidateQueries({ queryKey: ["me", "opportunities"] });
        // Accepting/declining is exactly what the crew calendar's accept filter reads, so it has to repaint:
        // an accepted invite appears, a passed one comes off.
        void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't send your response")),
    },
  );
}
